/**
 * SoundManager — Web Audio API synthesizer for pxFlow.
 *
 * All sounds are generated programmatically (no external audio files).
 * Respects browser autoplay policy by deferring AudioContext creation
 * until the first user interaction.
 *
 * Every sound effect applies subtle random micro-variations to pitch,
 * duration, volume, and envelope so that repeated plays feel natural
 * rather than robotic. Variations are tiny (a few percent) — the sound
 * character is always preserved.
 */

/** Random variation multipliers applied to each sound invocation. */
interface SoundVariation {
  /** Pitch multiplier, e.g. 0.97 – 1.03 (±3%). */
  pitch: number;
  /** Duration multiplier, e.g. 0.88 – 1.12 (±12%). */
  duration: number;
  /** Volume/gain multiplier, e.g. 0.93 – 1.07 (±7%). */
  volume: number;
  /** Envelope attack/decay multiplier, e.g. 0.90 – 1.10 (±10%). */
  envelope: number;
}

export class SoundManager {
  private ctx: AudioContext | null = null;
  private _muted = localStorage.getItem('pxflow-muted') === '1';
  /** Master volume. Readonly for now — volume control UI not yet implemented. */
  private readonly _volume = 0.5;
  private initialized = false;
  /** Cached noise buffers keyed by sample count to reduce GC pressure. */
  private noiseBufferCache = new Map<number, AudioBuffer>();

  /** Call on first user gesture to unlock audio. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    try {
      this.ctx = new AudioContext();
      if (this.ctx.state === 'suspended') {
        this.ctx.resume();
      }
    } catch (e) {
      // Web Audio not supported — sounds silently disabled
      if (import.meta.env?.DEV) console.warn('[SoundManager] AudioContext init failed:', e);
      this.ctx = null;
    }
  }

  get muted(): boolean {
    return this._muted;
  }

  set muted(v: boolean) {
    this._muted = v;
    localStorage.setItem('pxflow-muted', v ? '1' : '0');
  }

  toggleMute(): boolean {
    this._muted = !this._muted;
    localStorage.setItem('pxflow-muted', this._muted ? '1' : '0');
    return this._muted;
  }

  // ─── Sound Effects ─────────────────────────────────────────────────

  /** Short pop when player taps to dispatch an attacker. */
  tap(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(1200 * v.pitch, t + 0.04 * v.envelope);
      osc.frequency.exponentialRampToValueAtTime(600 * v.pitch, t + 0.08 * v.envelope);
      gain.gain.setValueAtTime(0.3 * v.volume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.1 * v.duration);
    });
  }

  /** Quick pew/zap when an attacker fires a projectile. */
  shoot(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(1400 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(200 * v.pitch, t + 0.06 * v.duration);
      gain.gain.setValueAtTime(0.12 * v.volume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.07 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.07 * v.duration);
    });
  }

  /** Satisfying pop when a cube is cleared. */
  cubeDestroy(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Noise burst + sine pop
      const bufferSize = Math.round(ctx.sampleRate * 0.05 * v.duration);
      const buffer = this.getNoiseBuffer(bufferSize);
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.15 * v.volume, t);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.05 * v.duration);
      // Bandpass to make it poppier
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 2000 * v.pitch;
      filter.Q.value = 1.5;
      noise.connect(filter).connect(noiseGain).connect(dest);
      noise.start(t);
      noise.stop(t + 0.05 * v.duration);

      // Sine pop
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(300 * v.pitch, t + 0.06 * v.envelope);
      oscGain.gain.setValueAtTime(0.18 * v.volume, t);
      oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.08 * v.duration);
      osc.connect(oscGain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.08 * v.duration);
    });
  }

  /** Higher-pitched bubble pop variant. */
  popHigh(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Quick sine chirp — higher register
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1100 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(500 * v.pitch, t + 0.05 * v.envelope);
      gain.gain.setValueAtTime(0.2 * v.volume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.06 * v.duration);
    });
  }

  /** Softer, rounder pop variant with triangle wave. */
  popSoft(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(750 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(350 * v.pitch, t + 0.07 * v.envelope);
      gain.gain.setValueAtTime(0.22 * v.volume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.09 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.09 * v.duration);
    });
  }

  /** Randomly play one of the three pop sound variants. */
  randomPop(): void {
    const r = Math.random();
    if (r < 0.34) {
      this.cubeDestroy();
    } else if (r < 0.67) {
      this.popHigh();
    } else {
      this.popSoft();
    }
  }

  /** Subtle whoosh when an attacker depletes ammo and fades away. */
  attackerExhausted(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Filtered noise whoosh
      const bufferSize = Math.round(ctx.sampleRate * 0.25 * v.duration);
      const buffer = this.getNoiseBuffer(bufferSize);
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(1000 * v.pitch, t);
      filter.frequency.exponentialRampToValueAtTime(4000 * v.pitch, t + 0.2 * v.duration);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.1 * v.volume, t);
      gain.gain.linearRampToValueAtTime(0.15 * v.volume, t + 0.05 * v.envelope);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25 * v.duration);
      noise.connect(filter).connect(gain).connect(dest);
      noise.start(t);
      noise.stop(t + 0.25 * v.duration);
    });
  }

  /** Ding/chime when an attacker completes a lap and moves to the stack. */
  lapComplete(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Two-tone chime
      const notes = [880 * v.pitch, 1100 * v.pitch];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = t + i * 0.08 * v.envelope;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.2 * v.volume, start + 0.01 * v.envelope);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.2 * v.duration);
        osc.connect(gain).connect(dest);
        osc.start(start);
        osc.stop(start + 0.2 * v.duration);
      });
    });
  }

  /** Buzz/thud when player taps but conveyor is full. */
  error(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(150 * v.pitch, t);
      osc.frequency.linearRampToValueAtTime(80 * v.pitch, t + 0.15 * v.duration);
      gain.gain.setValueAtTime(0.15 * v.volume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.15 * v.duration);
    });
  }

  /** Celebratory jingle when the player wins a level. */
  levelWin(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Ascending arpeggio: C5 E5 G5 C6
      const notes = [523, 659, 784, 1047];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq * v.pitch;
        const start = t + i * 0.1 * v.envelope;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.2 * v.volume, start + 0.02 * v.envelope);
        gain.gain.exponentialRampToValueAtTime(0.01, start + 0.35 * v.duration);
        osc.connect(gain).connect(dest);
        osc.start(start);
        osc.stop(start + 0.35 * v.duration);
      });

      // Final sustain shimmer
      const shimmer = ctx.createOscillator();
      const shimmerGain = ctx.createGain();
      shimmer.type = 'triangle';
      shimmer.frequency.value = 1047 * v.pitch;
      const shimmerStart = t + 0.35 * v.envelope;
      shimmerGain.gain.setValueAtTime(0.15 * v.volume, shimmerStart);
      shimmerGain.gain.exponentialRampToValueAtTime(0.001, shimmerStart + 0.6 * v.duration);
      shimmer.connect(shimmerGain).connect(dest);
      shimmer.start(shimmerStart);
      shimmer.stop(shimmerStart + 0.6 * v.duration);
    });
  }

  /** Sad descending tone for game over. */
  levelLose(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Descending minor: C5 Ab4 F4 D4
      const notes = [523, 415, 349, 294];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq * v.pitch;
        const start = t + i * 0.15 * v.envelope;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.18 * v.volume, start + 0.02 * v.envelope);
        gain.gain.exponentialRampToValueAtTime(0.01, start + 0.4 * v.duration);
        osc.connect(gain).connect(dest);
        osc.start(start);
        osc.stop(start + 0.4 * v.duration);
      });
    });
  }

  /** Whoosh/acceleration sound when win-state speed-up activates. */
  speedUp(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      // Rising sine sweep
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(200 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(1200 * v.pitch, t + 0.3 * v.duration);
      gain.gain.setValueAtTime(0.1 * v.volume, t);
      gain.gain.linearRampToValueAtTime(0.2 * v.volume, t + 0.15 * v.envelope);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.35 * v.duration);

      // Noise whoosh layer
      const bufferSize = Math.round(ctx.sampleRate * 0.3 * v.duration);
      const buffer = this.getNoiseBuffer(bufferSize);
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(500 * v.pitch, t);
      filter.frequency.exponentialRampToValueAtTime(3000 * v.pitch, t + 0.3 * v.duration);
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.05 * v.volume, t);
      noiseGain.gain.linearRampToValueAtTime(0.1 * v.volume, t + 0.15 * v.envelope);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.35 * v.duration);
      noise.connect(filter).connect(noiseGain).connect(dest);
      noise.start(t);
      noise.stop(t + 0.35 * v.duration);
    });
  }

  /** Subtle slide/reveal sound for queue peek activation. */
  peek(): void {
    this.play((ctx, dest, t) => {
      const v = this.variation();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(400 * v.pitch, t);
      osc.frequency.exponentialRampToValueAtTime(800 * v.pitch, t + 0.08 * v.envelope);
      gain.gain.setValueAtTime(0.12 * v.volume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12 * v.duration);
      osc.connect(gain).connect(dest);
      osc.start(t);
      osc.stop(t + 0.12 * v.duration);
    });
  }

  // ─── Internal ──────────────────────────────────────────────────────

  /**
   * Get or create a cached noise buffer of the given sample count.
   * Reuses buffers to avoid allocating new Float32Arrays per sound play.
   * The buffer is filled with white noise — callers shape it via gain/filter.
   */
  private getNoiseBuffer(sampleCount: number): AudioBuffer {
    const key = sampleCount;
    let buffer = this.noiseBufferCache.get(key);
    if (!buffer) {
      buffer = this.ctx!.createBuffer(1, sampleCount, this.ctx!.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < sampleCount; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      this.noiseBufferCache.set(key, buffer);
    }
    return buffer;
  }

  /**
   * Generate subtle random variation multipliers for a single sound play.
   * All values hover close to 1.0 so the sound character is preserved.
   */
  private variation(): SoundVariation {
    return {
      pitch: 1 + (Math.random() - 0.5) * 0.06,    // ±3%
      duration: 1 + (Math.random() - 0.5) * 0.24,  // ±12%
      volume: 1 + (Math.random() - 0.5) * 0.14,    // ±7%
      envelope: 1 + (Math.random() - 0.5) * 0.20,  // ±10%
    };
  }

  private play(
    build: (ctx: AudioContext, dest: AudioNode, t: number) => void,
  ): void {
    if (this._muted || !this.ctx) return;
    try {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      const master = this.ctx.createGain();
      master.gain.value = this._volume;
      master.connect(this.ctx.destination);
      build(this.ctx, master, this.ctx.currentTime);
    } catch (e) {
      // Swallow errors — never crash the game due to audio
      if (import.meta.env?.DEV) console.warn('[SoundManager] play() error:', e);
    }
  }
}

/** Singleton instance shared across all scenes. */
export const soundManager = new SoundManager();
